<script setup lang="ts">
const props = defineProps<{
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
}>();
</script>

<template>
  <button
    :type="props.type ?? 'button'"
    :disabled="props.disabled || props.loading"
    :class="[
      'btn',
      `btn--${props.variant ?? 'primary'}`,
      props.fullWidth && 'btn--full-width',
    ]"
    :aria-busy="props.loading"
  >
    <span v-if="props.loading" class="btn__spinner" aria-hidden="true" />
    <slot />
  </button>
</template>

<style scoped>
@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease,
    box-shadow 120ms ease;
}

.btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--border-focus);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn--full-width {
  width: 100%;
}

.btn--primary {
  background: var(--color-surface-brand);
  color: var(--color-text-inverted);
}

.btn--primary:hover:not(:disabled) {
  background: var(--color-surface-brand-hover);
}

.btn--secondary {
  background: var(--color-surface-raised);
  color: var(--color-text-strong);
  border-color: var(--border-strong);
}

.btn--secondary:hover:not(:disabled) {
  background: var(--color-interactive-hover);
}

.btn--ghost {
  background: transparent;
  color: var(--color-text-strong);
}

.btn--ghost:hover:not(:disabled) {
  background: var(--color-interactive-hover-strong);
}

.btn__spinner {
  width: 0.75rem;
  height: 0.75rem;
  margin-right: var(--space-2);
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: var(--radius-full);
  animation: btn-spin 700ms linear infinite;
}
</style>
